import React, { FC, FocusEventHandler, MouseEvent, useState } from 'react';
import { KeyboardTimePickerProps } from '@material-ui/pickers';
import { parse } from 'date-fns';
import { IconButton, makeStyles, TextField } from '@material-ui/core';
import { TextFieldSkeleton } from '../Skeletons';
import clsx from 'clsx';

export type TimeFieldChangeHandler = (
  value: string,
  values: {
    date: Date;
    inputValue: string;
    stringValue: string;
  },
) => void;

const useStyle = makeStyles(() => ({
  input: {
    //this is a hack removes the placeholder when there is no value and is
    // not focused done temporarily instead of refactoring this use TimeField
    '&:not(:focus-within) input': {
      [[
        '&::-webkit-datetime-edit-ampm-field',
        '&::-webkit-datetime-edit-hour-field',
        '&::-webkit-datetime-edit-minute-field',
        '&::-webkit-datetime-edit-text',
      ].join(', ')]: {
        color: 'transparent',
      },
    },
  },
}));

export type TimeFieldProps = {
  value?: Date | string | null;
  onChange?: TimeFieldChangeHandler;
  /** controls whether clock adornment that opens clock time picker is visible */
  hideClock?: boolean;
  label?: string;
  loading?: boolean;
  'data-testid'?: string;
} & Omit<KeyboardTimePickerProps, 'value' | 'onChange'>;

export const TimeField: FC<TimeFieldProps> = ({
  onChange: onChangeProp,
  onFocus = () => undefined,
  value: valueProp,
  loading = false,
  hideClock = true,
  ampm = true,
  InputAdornmentProps: InputAdornmentPropsProp = {},
  InputProps = {},
  ...props
}: TimeFieldProps) => {
  const classes = useStyle();
  const { ref, label, fullWidth, className } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const output = event.target.value;
    const date = parse(output, 'HH:mm', new Date());

    onChangeProp?.(output, { date: date as Date, inputValue: output, stringValue: output });
    handlePickerClose();
  };

  const handleFocus: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    onFocus(event);
  };

  const handlePickerClose = () => {
    setAnchorEl(null);
  };

  const handlePickerOpen = (event: MouseEvent<HTMLButtonElement>) => {
    !anchorEl && setAnchorEl(event.currentTarget);
  };

  if (loading) {
    return <TextFieldSkeleton label={label} />;
  }

  const inputPropsAvailable = (overridden: boolean) => {
    if (overridden) {
      return {
        endAdornment: (
          <>
            {InputProps?.endAdornment}
            <IconButton size="small" onClick={handlePickerOpen} style={{ bottom: 4 }}></IconButton>
          </>
        ),
      };
    }
    return {};
  };

  return (
    <TextField
      value={valueProp ?? ''}
      data-testid={props['data-testid']}
      className={`${clsx({
        [classes.input]: !valueProp,
      })} ${className}`}
      inputRef={ref}
      label={label}
      id={`${label}-id`}
      type="time"
      onChange={handleChange}
      onFocus={handleFocus}
      InputProps={{ ...inputPropsAvailable(props['data-isoverridden']), ...InputProps }}
      fullWidth={fullWidth}
    />
  );
};

export default TimeField;
